import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CLIENT_SECTION_LABELS, CLIENT_STAGES, CLIENT_STAGE_LABELS, RARITY_DIMENSION_LABELS, REACTION_KINDS, REACTION_KIND_LABELS, type ClientSection, type RarityDimension, type ReactionKind } from '@raro/domain';
import { Avatar, Button, cx, EmptyState, Figure, Icon, Label, RaroBrand, ScoreBars, Select, Serif, Stepper, Tag, Textarea, Thumb } from '@raro/ui';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import ocean from '../assets/ocean.jpg';
import { AddSourceDrawer } from '../components/AddSourceDrawer';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatMoney, daysLeft } from '../lib/format';
import { EffectCharts } from '../components/VerSections';
import type { ClientRoom, RoomComment, RoomItem, RoomProject, RoomDeliverable } from '../lib/types';
import { CLAIM_KIND_LABELS, DELIVERABLE_FORMAT_LABELS, DELIVERABLE_STATUS_LABELS, EFFECT_SOURCE_LABELS, RARITY_DIMENSIONS, hasRoleAtLeast, type ClaimKind, type EffectSource, type InitiativeStatus, type MembershipRole } from '@raro/domain';

const NAV: { key: 'overview' | ClientSection | 'cycle'; label: string }[] = [
  { key: 'overview', label: 'Visão geral' },
  { key: 'cycle', label: 'Ciclo atual' },
  { key: 'wall', label: 'Parede' },
  { key: 'raro', label: 'O Raro' },
  { key: 'make', label: 'Fazer' },
  { key: 'results', label: 'Resultados' },
  { key: 'library', label: 'Biblioteca' },
];

export function ClientRoomPage() {
  const { slug = '' } = useParams();
  const { me, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const room = useQuery({ queryKey: ['client-room', slug], queryFn: () => api.get<ClientRoom>(`/client-room/${slug}`) });
  const [projectId, setProjectId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const view = (params.get('v') as (typeof NAV)[number]['key'] | null) ?? 'overview';
  const setView = (v: string) => setParams({ v, ...(projectId ? { p: projectId } : {}) });

  useEffect(() => {
    if (room.data && !projectId) setProjectId(params.get('p') ?? room.data.projects[0]?.id ?? null);
  }, [room.data, projectId, params]);

  if (room.isPending) return <div className="rr-page rr-faint">…</div>;
  if (room.isError || !room.data) return <div className="rr-page"><p className="rr-field__error">não encontramos esta sala.</p></div>;
  const r = room.data;
  const p = r.projects.find((x) => x.id === projectId) ?? r.projects[0] ?? null;

  return (
    <div className="rr-client-shell">
      <aside className="rr-client-nav">
        <div style={{ padding: '0 var(--rr-space-5)' }}><RaroBrand /></div>
        <div className="rr-client-nav__org">{r.organization.name}</div>
        <nav className="rr-sidebar__nav">
          {NAV.map((n) => (
            <button key={n.key} type="button" className="rr-nav-item" aria-current={view === n.key ? 'page' : undefined} onClick={() => setView(n.key)}>
              <span>{n.label}</span>
            </button>
          ))}
        </nav>
        <div className="rr-sidebar__foot">
          {r.internal && <Link to={`/projetos/${p?.id ?? ''}`} className="rr-label">← interno</Link>}
          {me && <Button variant="quiet" size="sm" onClick={logout} title={`${me.email} · sair`}><Avatar name={me.name} accent /></Button>}
        </div>
      </aside>
      <div className="rr-client-main">
        <header className="rr-client-head">
          <h1 className="rr-client-title"><b>RARO</b><span className="rr-faint">×</span><b>{r.organization.name.toUpperCase()}</b></h1>
          <div className="rr-row">
            {r.projects.length > 1 && (
              <Select style={{ width: 'auto', height: 32, fontSize: 'var(--rr-text-xs)' }} value={p?.id ?? ''} onChange={(e) => { setProjectId(e.target.value); setParams({ v: view, p: e.target.value }); }}>
                {r.projects.map((x) => <option key={x.id} value={x.id}>{x.name}{x.cycle ? ` · ${x.cycle.name}` : ''}</option>)}
              </Select>
            )}
            {p && <Button size="sm" variant="primary" onClick={() => setAdding(true)}>+ adicionar</Button>}
          </div>
        </header>
        {!p ? (
          <div className="rr-page"><EmptyState title="ainda não há projetos publicados." /></div>
        ) : (
          <div className="rr-page rr-stack rr-stack--8">
            {view === 'overview' && <Overview p={p} onGo={setView} />}
            {view === 'cycle' && <Section title={CLIENT_SECTION_LABELS.seeing} sub={`${p.cycle?.name ?? ''} · ${CLIENT_STAGE_LABELS[p.clientStage]}`}><Findings items={p.sections.seeing} /></Section>}
            {view === 'wall' && <Section title="Parede" sub="fotos, entrevistas, dados, referências e anotações"><WallGrid items={p.sections.wall} /></Section>}
            {view === 'raro' && <Section title="O Raro" sub="hipóteses em investigação. construídas a partir dos achados."><Hypotheses items={p.sections.raro} /></Section>}
            {view === 'make' && <Section title="Fazer" sub="o que está sendo construído"><Pecas items={p.deliverables} canDecide={hasRoleAtLeast(r.role as MembershipRole, 'client_admin')} slug={slug} /><Deliverables items={p.sections.make} canDecide={hasRoleAtLeast(r.role as MembershipRole, 'client_admin')} slug={slug} /></Section>}
            {view === 'results' && <Section title="Resultados" sub="o que aconteceu"><Results items={p.sections.results} /></Section>}
            {view === 'library' && <Section title="Biblioteca" sub="arquivos e materiais deste ciclo"><Library items={p.sections.library} /></Section>}
          </div>
        )}
      </div>
      {p && <AddSourceDrawer open={adding} onClose={() => setAdding(false)} organizationId={r.organization.id} projectId={p.id} />}
    </div>
  );
}

function Section({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section className="rr-section">
      <div className="rr-stack rr-stack--1"><h2 className="rr-title">{title}</h2>{sub && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>{sub}</span>}</div>
      {children}
    </section>
  );
}

function Overview({ p, onGo }: { p: RoomProject; onGo: (v: string) => void }) {
  const left = daysLeft(p.cycle?.endsAt);
  const stageIndex = CLIENT_STAGES.indexOf(p.clientStage);
  return (
    <>
      <div className="rr-hero rr-hero--photo">
        <img className="rr-hero__img" src={p.cover?.url ?? ocean} alt="" aria-hidden="true" />
        <Label>O que estamos tentando mudar</Label>
        <p className="rr-hero__q">{p.currentQuestion ?? p.name}</p>
      </div>
      <div className="rr-client-stage">
        <Stepper steps={CLIENT_STAGES.map((s) => CLIENT_STAGE_LABELS[s])} current={stageIndex} onStep={(i) => onGo((['cycle', 'raro', 'make', 'results'] as const)[i]!)} />
        <div className="rr-stack rr-stack--1" style={{ alignItems: 'flex-end' }}>
          <span className="rr-label rr-label--strong">Estamos em <span style={{ color: 'var(--rr-accent)' }}>{CLIENT_STAGE_LABELS[p.clientStage]}</span></span>
          {left !== null && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{left} dias restantes neste ciclo</span>}
          {p.cycle && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{p.cycle.name}{p.cycle.startsAt ? ` · desde ${formatDate(p.cycle.startsAt)}` : ''}</span>}
        </div>
      </div>
      <section className="rr-section">
        <div className="rr-section__head"><Label>O que estamos vendo</Label><button type="button" className="rr-label rr-label--strong" onClick={() => onGo('cycle')}>ver tudo →</button></div>
        <Findings items={p.sections.seeing.slice(0, 3)} />
      </section>
      {p.sections.raro.length > 0 && (
        <section className="rr-section">
          <div className="rr-section__head"><Label>O Raro · hipóteses selecionadas</Label><button type="button" className="rr-label rr-label--strong" onClick={() => onGo('raro')}>ver →</button></div>
          <Hypotheses items={p.sections.raro.slice(0, 3)} compact />
        </section>
      )}
      <RarityIndex items={p.sections.raro} />
    </>
  );
}

function Findings({ items }: { items: RoomItem[] }) {
  if (items.length === 0) return <EmptyState title="ainda não publicamos achados neste ciclo." />;
  return (
    <div className="rr-findings">
      {items.map((it, i) => it.entityType === 'synthesis' ? (
        <article key={it.id} className="rr-finding rr-finding--bulletin">
          <span className="rr-label" style={{ color: 'var(--rr-accent)' }}>{String(it.entity.cycleName ?? 'período')}</span>
          <div className="rr-finding__title">{it.title ?? String(it.entity.title ?? '')}</div>
          {(it.entity.whatWeSaw as string[] | undefined)?.length ? <div><span className="rr-label">O que vimos</span><ul className="rr-bulletin__list">{(it.entity.whatWeSaw as string[]).map((x, k) => <li key={k}>{x}</li>)}</ul></div> : null}
          {it.entity.whatHappened ? <div><span className="rr-label">O que aconteceu</span><p className="rr-finding__text">{String(it.entity.whatHappened)}</p></div> : null}
          {(it.entity.whatWeLearned as string[] | undefined)?.length ? <div><span className="rr-label">O que aprendemos</span><ul className="rr-bulletin__list">{(it.entity.whatWeLearned as string[]).map((x, k) => <li key={k}><Serif>{x}</Serif></li>)}</ul></div> : null}
          {it.entity.nextCycle ? <div><span className="rr-label">Próximo período</span><p className="rr-finding__text">{String(it.entity.nextCycle)}</p></div> : null}
          <Interaction it={it} />
        </article>
      ) : (
        <article key={it.id} className={cx('rr-finding', it.image && 'rr-finding--photo')}>
          {it.image && <Figure src={it.image.url} ratio="16 / 10" radius="none"><span className="rr-finding__num" style={{ color: 'var(--rr-text)' }}>{String(i + 1).padStart(2, '0')}</span></Figure>}
          <div className={it.image ? 'rr-finding__body' : 'rr-stack rr-stack--3'}>
            {!it.image && <span className="rr-finding__num">{String(i + 1).padStart(2, '0')}</span>}
            <div className="rr-finding__title">{it.title ?? it.entity.text ?? it.entity.content ?? it.entity.title}</div>
            {(it.text ?? (it.title ? it.entity.text ?? it.entity.content : null)) && <p className="rr-finding__text">{it.text ?? it.entity.text ?? it.entity.content}</p>}
            <Interaction it={it} />
          </div>
        </article>
      ))}
    </div>
  );
}

function WallGrid({ items }: { items: RoomItem[] }) {
  if (items.length === 0) return <EmptyState title="a parede ainda está vazia." />;
  return (
    <div className="rr-wallgrid">
      {items.map((it) => {
        const isEvidence = it.entityType === 'evidence';
        return (
          <article key={it.id} className={cx('rr-wall-item', `rr-wall-item--${it.entityType}`)} style={{ position: 'relative', cursor: 'default' }}>
            <div className="rr-wall-item__kind"><span>{it.entityType === 'evidence' ? 'o que ouvimos' : it.entityType === 'signal' ? 'sinal' : it.entityType === 'hypothesis' ? 'hipótese' : it.entityType}</span></div>
            {it.entity.title && !isEvidence && <div className="rr-wall-item__title">{it.title ?? it.entity.title}</div>}
            <div className="rr-wall-item__text">{isEvidence ? `“${it.text ?? it.entity.content}”` : it.text ?? it.entity.text ?? it.entity.statement ?? it.entity.description}</div>
            <Interaction it={it} compact />
          </article>
        );
      })}
    </div>
  );
}

function Hypotheses({ items, compact }: { items: RoomItem[]; compact?: boolean }) {
  if (items.length === 0) return <EmptyState title="ainda não há hipóteses publicadas." />;
  return (
    <div className="rr-findings">
      {items.map((it, i) => (
        <article key={it.id} className={cx('rr-finding', it.image && 'rr-finding--photo')}>
          {it.image && <Figure src={it.image.url} ratio="16 / 10" radius="none"><span className="rr-finding__num" style={{ color: 'var(--rr-accent)' }}>{String(i + 1).padStart(2, '0')}</span></Figure>}
          <div className={it.image ? 'rr-finding__body' : 'rr-stack rr-stack--3'}>
            {!it.image && <span className="rr-finding__num">{String(i + 1).padStart(2, '0')}</span>}
            <div className="rr-finding__title" style={{ color: 'var(--rr-accent)' }}>{it.title ?? it.entity.title}</div>
            <p className="rr-finding__text"><Serif italic>{it.text ?? it.entity.statement}</Serif></p>
            {!compact && it.entity.scores && Object.keys(it.entity.scores).length > 0 && (
              <ScoreBars rows={(Object.entries(it.entity.scores) as [RarityDimension, number][]).map(([k, v]) => ({ label: RARITY_DIMENSION_LABELS[k] ?? k, value: v }))} />
            )}
            <Interaction it={it} />
          </div>
        </article>
      ))}
    </div>
  );
}

/** Índice de Raridade™: média das dimensões do O RARO nas hipóteses publicadas. */
function RarityIndex({ items }: { items: RoomItem[] }) {
  const scored = items.map((it) => it.entity.scores ?? {}).filter((sc) => Object.keys(sc).length > 0);
  if (scored.length === 0) return null;
  const rows = RARITY_DIMENSIONS.map((d) => {
    const vals = scored.map((sc) => sc[d]).filter((v): v is number => typeof v === 'number');
    return { label: RARITY_DIMENSION_LABELS[d], value: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null };
  });
  const all = rows.map((r) => r.value).filter((v): v is number => v !== null);
  const index = all.length ? Math.round(all.reduce((a, b) => a + b, 0) / all.length) : null;
  return (
    <section className="rr-section">
      <div className="rr-section__head"><Label>Índice de Raridade™</Label><span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>acompanhamento dos territórios que tornam a marca mais difícil de confundir, copiar ou substituir.</span></div>
      <div className="rr-results-split">
        <ScoreBars rows={rows} />
        <div className="rr-stat rr-stat--tile"><div className="rr-stat__value" style={{ color: 'var(--rr-accent)' }}>{index ?? '—'}</div><div className="rr-stat__label">índice médio · {scored.length} hipótese(s) julgada(s)</div></div>
      </div>
    </section>
  );
}

const STATUS_PCT: Record<InitiativeStatus | string, number> = { planned: 10, in_progress: 55, done: 85, measured: 100, dropped: 0, recorded: 20, reverted: 0, approved: 100, published: 100, draft: 30, reviewed: 60 };
const STATUS_LBL: Record<string, string> = { planned: 'planejada', in_progress: 'em andamento', done: 'feita', measured: 'medida', dropped: 'abandonada', recorded: 'decidido', reverted: 'revertida', approved: 'aprovada', published: 'publicada', draft: 'rascunho', reviewed: 'em revisão' };

function Deliverables({ items, canDecide, slug }: { items: RoomItem[]; canDecide: boolean; slug: string }) {
  if (items.length === 0) return <EmptyState title="nada em produção ainda." />;
  const solutions = items.filter((i) => i.entityType === 'solution');
  const claims = items.filter((i) => i.entityType === 'claim');
  const rest = items.filter((i) => i.entityType !== 'solution' && i.entityType !== 'claim');
  return (
    <div className="rr-stack rr-stack--5">
      {solutions.map((it) => (
        <article key={it.id} className="rr-solution rr-solution--room">
          <Label tone="accent">A Solução</Label>
          <p className="rr-solution__position">{it.title ?? String(it.entity.position ?? '')}</p>
          {(it.text ?? it.entity.summary) != null && <p className="rr-muted">{String(it.text ?? it.entity.summary)}</p>}
          <Interaction it={it} compact />
        </article>
      ))}
      {claims.length > 0 && (
        <div className="rr-stack rr-stack--2">
          {claims.map((it) => (
            <div key={it.id} className="rr-claim rr-claim--room">
              <span className="rr-label">{CLAIM_KIND_LABELS[(it.entity.kind as ClaimKind) ?? 'finding']}</span>
              <p className="rr-claim__text">{it.title ?? String(it.entity.text ?? '')}</p>
              {(it.text ?? it.entity.detail) != null && <p className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>{String(it.text ?? it.entity.detail)}</p>}
              {it.entity.provenance != null && (it.entity.provenance as { supports: number }).supports > 0 && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>sustentada por {(it.entity.provenance as { supports: number }).supports} evidência(s){(it.entity.provenance as { contradicts: number }).contradicts ? ` · ${(it.entity.provenance as { contradicts: number }).contradicts} em contrário` : ''}</span>}
              <Interaction it={it} compact />
            </div>
          ))}
        </div>
      )}
      {rest.map((it) => (
        <div key={it.id} className={cx('rr-deliv', it.image && 'rr-deliv--photo')}>
          {it.image && <Thumb src={it.image.thumbUrl ?? it.image.url} size={56} />}
          <div className="rr-stack rr-stack--1">
            <span style={{ fontWeight: 500 }}>{it.title ?? it.entity.title}</span>
            {(it.text ?? it.entity.description ?? it.entity.decision) != null && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{String(it.text ?? it.entity.description ?? it.entity.decision)}</span>}
            {it.entity.expectedEffect != null && it.entity.expectedEffect !== (it.text ?? it.entity.description) && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>efeito esperado: {String(it.entity.expectedEffect)}{it.entity.executor ? ` · executa: ${it.entity.executor === 'client' ? 'vocês' : it.entity.executor === 'raro' ? 'Raro' : 'juntos'}` : ''}</span>}
            {it.entityType === 'initiative' && Number(it.entity.budgetEstimated ?? 0) > 0 && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>estimativa de produção: <b style={{ color: 'var(--rr-text)' }}>{formatMoney(Number(it.entity.budgetEstimated))}</b></span>}
            {it.entityType === 'initiative' && <ClientDecisionBox it={it} canDecide={canDecide} slug={slug} />}
            <Interaction it={it} compact />
          </div>
          <div className="rr-deliv__bar" style={{ ['--v' as string]: `${STATUS_PCT[it.entity.status ?? ''] ?? 0}%` }} />
          <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)', textAlign: 'right' }}>{STATUS_LBL[it.entity.status ?? ''] ?? it.entity.status}</span>
        </div>
      ))}
    </div>
  );
}

/** Peças para o cliente ver e aprovar: a partir de "com o cliente". Preview da versão atual, aprovar ou pedir ajuste com nota. */
function Pecas({ items, canDecide, slug }: { items: RoomDeliverable[]; canDecide: boolean; slug: string }) {
  if (items.length === 0) return null;
  const waiting = items.filter((d) => d.status === 'client_review');
  const done = items.filter((d) => d.status !== 'client_review');
  return (
    <div className="rr-stack rr-stack--5" style={{ marginBottom: 'var(--rr-space-6)' }}>
      {waiting.length > 0 && (
        <div className="rr-stack rr-stack--3">
          <Label tone="accent">Para vocês aprovarem · {waiting.length}</Label>
          <div className="rr-findings">{waiting.map((d) => <PecaRoomCard key={d.id} d={d} canDecide={canDecide} slug={slug} />)}</div>
        </div>
      )}
      {done.length > 0 && (
        <div className="rr-stack rr-stack--3">
          <Label>Peças aprovadas e no ar · {done.length}</Label>
          <div className="rr-findings">{done.map((d) => <PecaRoomCard key={d.id} d={d} canDecide={canDecide} slug={slug} />)}</div>
        </div>
      )}
    </div>
  );
}

function PecaRoomCard({ d, canDecide, slug }: { d: RoomDeliverable; canDecide: boolean; slug: string }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [declining, setDeclining] = useState(false);
  const decide = useMutation({ mutationFn: (decision: 'approved' | 'declined') => api.post(`/client-room/deliverables/${d.id}/decision`, { decision, note: note || undefined }), onSuccess: () => { setDeclining(false); setNote(''); void qc.invalidateQueries({ queryKey: ['client-room', slug] }); } });
  return (
    <article className={cx('rr-finding', d.file?.isImage && 'rr-finding--photo')}>
      {d.file?.isImage && <Figure src={d.file.url} ratio="16 / 10" radius="none"><span className="rr-label" style={{ color: 'var(--rr-text-2)' }}>v{d.version}</span></Figure>}
      <div className={d.file?.isImage ? 'rr-finding__body' : 'rr-stack rr-stack--3'}>
        <div className="rr-row rr-row--wrap" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)', gap: 'var(--rr-space-2)' }}>
          <Tag tone={d.status === 'client_review' ? 'accent' : 'outline'}>{d.status === 'client_review' ? 'aguardando vocês' : DELIVERABLE_STATUS_LABELS[d.status]}</Tag>
          <span>{DELIVERABLE_FORMAT_LABELS[d.format]}</span>
          {d.channel && <span>· {d.channel.name}</span>}
          {d.dueAt && <span>· {d.status === 'live' && d.liveAt ? `no ar desde ${formatDate(d.liveAt)}` : `entrega ${formatDate(d.dueAt)}`}</span>}
        </div>
        <div className="rr-finding__title">{d.title}</div>
        {d.description && <p className="rr-finding__text">{d.description}</p>}
        {d.initiative && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>parte de: {d.initiative.title}</span>}
        <Link to={`/client/${slug}/revisao/${d.id}`} className="rr-action">{d.status === 'client_review' ? 'abrir, marcar e aprovar →' : 'abrir a peça →'}{d.review?.openComments ? ` · ${d.review.openComments} comentário(s)` : ''}</Link>
        {d.file && !d.file.isImage && <a className="rr-action" href={d.file.url} target="_blank" rel="noreferrer">abrir {d.file.name} →</a>}
        {d.url && <a className="rr-action" href={d.url} target="_blank" rel="noreferrer">abrir link →</a>}
        {d.clientDecision && d.status !== 'client_review' && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{d.clientDecision === 'approved' ? 'aprovada por vocês' : 'vocês pediram ajuste'}{d.clientDecidedAt ? ` · ${formatDate(d.clientDecidedAt)}` : ''}{d.clientNote ? ` · “${d.clientNote}”` : ''}</span>}
        {d.status === 'client_review' && (canDecide ? (
          <div className="rr-stack rr-stack--2">
            {declining && <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="o que precisa mudar?" autoFocus />}
            <div className="rr-row" style={{ gap: 6 }}>
              {!declining && <Button size="sm" variant="primary" onClick={() => decide.mutate('approved')} disabled={decide.isPending}>aprovar</Button>}
              {!declining && <Button size="sm" variant="quiet" onClick={() => setDeclining(true)}>pedir ajuste</Button>}
              {declining && <Button size="sm" variant="primary" onClick={() => decide.mutate('declined')} disabled={note.trim().length < 2 || decide.isPending}>enviar pedido</Button>}
              {declining && <Button size="sm" variant="quiet" onClick={() => setDeclining(false)}>voltar</Button>}
            </div>
            {decide.isError && <span className="rr-field__error">{(decide.error as Error).message}</span>}
          </div>
        ) : (
          <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>aguardando quem responde pela empresa</span>
        ))}
      </div>
    </article>
  );
}

/** O cliente bate o martelo do lado dele: aprova ou declina a iniciativa. Fica registrado e avisa a Raro. */
function ClientDecisionBox({ it, canDecide, slug }: { it: RoomItem; canDecide: boolean; slug: string }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [declining, setDeclining] = useState(false);
  const decide = useMutation({ mutationFn: (decision: 'approved' | 'declined') => api.post(`/client-room/initiatives/${it.entityId}/decision`, { decision, note: note || undefined }), onSuccess: () => { setDeclining(false); setNote(''); void qc.invalidateQueries({ queryKey: ['client-room', slug] }); } });
  const d = it.entity.clientDecision as 'approved' | 'declined' | null | undefined;
  if (d) {
    return (
      <span className="rr-row rr-row--wrap" style={{ fontSize: 'var(--rr-text-xs)', gap: 'var(--rr-space-2)' }}>
        <Tag tone={d === 'approved' ? 'accent' : 'outline'}>{d === 'approved' ? 'aprovada por vocês' : 'declinada por vocês'}</Tag>
        {it.entity.clientNote != null && <span className="rr-faint">“{String(it.entity.clientNote)}”</span>}
        {canDecide && <button type="button" className="rr-react" onClick={() => decide.mutate(d === 'approved' ? 'declined' : 'approved')}>{d === 'approved' ? 'voltar atrás' : 'aprovar afinal'}</button>}
      </span>
    );
  }
  if (!canDecide) return <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>aguardando a decisão de quem responde pela empresa</span>;
  return (
    <div className="rr-stack rr-stack--2">
      {declining && <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="por quê? ajuda a Raro a ajustar." autoFocus />}
      <div className="rr-row" style={{ gap: 6 }}>
        {!declining && <Button size="sm" variant="primary" onClick={() => decide.mutate('approved')} disabled={decide.isPending}>aprovar</Button>}
        {!declining && <Button size="sm" variant="quiet" onClick={() => setDeclining(true)}>não agora</Button>}
        {declining && <Button size="sm" variant="primary" onClick={() => decide.mutate('declined')} disabled={decide.isPending}>confirmar que não</Button>}
        {declining && <Button size="sm" variant="quiet" onClick={() => setDeclining(false)}>voltar</Button>}
      </div>
      {decide.isError && <span className="rr-field__error">{(decide.error as Error).message}</span>}
    </div>
  );
}

function Results({ items }: { items: RoomItem[] }) {
  if (items.length === 0) return <EmptyState title="ainda não medimos efeitos." />;
  const effects = items.filter((i) => i.entityType === 'effect');
  const rest = items.filter((i) => i.entityType !== 'effect');
  const asEffects = effects.map((it) => ({ id: it.id, projectId: '', initiativeId: null, kind: 'quantitative' as const, text: String(it.text ?? it.entity.text ?? ''), source: (it.entity.source as EffectSource) ?? 'other', metric: (it.entity.metric as string | null) ?? null, before: it.entity.before ?? null, after: it.entity.after ?? null, unit: (it.entity.unit as string | null) ?? null, direction: Number(it.entity.direction ?? 0), observedAt: String(it.entity.observedAt ?? it.publishedAt ?? ''), confidence: null, createdAt: '' }));
  return (
    <div className="rr-stack rr-stack--6">
      {effects.length > 0 && <EffectCharts effects={asEffects} />}
      {effects.length > 0 && (
        <div className="rr-stack rr-stack--3">
          <Label>O que mudou</Label>
          {effects.map((it) => (
            <div key={it.id} className="rr-list-row" style={{ gridTemplateColumns: '1fr auto' }}>
              <div>
                <div className="rr-list-row__title">{it.text ?? it.entity.text}</div>
                <div className="rr-list-row__meta"><span>{EFFECT_SOURCE_LABELS[(it.entity.source as EffectSource) ?? 'other']}</span>{it.entity.metric && <span>{String(it.entity.metric)}{it.entity.before != null && it.entity.after != null ? ` · ${it.entity.before} → ${it.entity.after}` : ''}</span>}</div>
                <Interaction it={it} compact />
              </div>
            </div>
          ))}
        </div>
      )}
      {rest.length > 0 && (
        <div className="rr-stack rr-stack--3">
          <Label>O que aprendemos</Label>
          {rest.map((it) => (
            <div key={it.id} className="rr-learning"><p className="rr-learning__text">{String(it.text ?? it.entity.text ?? it.entity.belief ?? '')}</p>{it.entity.outcome && <Tag tone="outline">{String(it.entity.outcome)}</Tag>}<Interaction it={it} compact /></div>
          ))}
        </div>
      )}
    </div>
  );
}

function Library({ items }: { items: RoomItem[] }) {
  if (items.length === 0) return <EmptyState title="nenhum arquivo compartilhado ainda." />;
  return (
    <div className="rr-list">
      {items.map((it) => (
        <div key={it.id} className="rr-list-row" style={{ gridTemplateColumns: '1fr auto' }}>
          <div>
            <div className="rr-list-row__title">{it.title ?? it.entity.title}</div>
            <div className="rr-list-row__meta">{it.text ?? it.entity.description}{it.entity.file && <span>{(it.entity.file.sizeBytes / 1024).toFixed(0)} KB</span>}</div>
          </div>
          {it.entity.file ? <a className="rr-action" href={`${import.meta.env.VITE_API_URL ?? 'http://localhost:4000'}${it.entity.file.url}`} target="_blank" rel="noreferrer">abrir →</a> : it.entity.url ? <a className="rr-action" href={String(it.entity.url)} target="_blank" rel="noreferrer">abrir link →</a> : null}
        </div>
      ))}
    </div>
  );
}

/** Reações + comentários de um item publicado. Interno e cliente conversam aqui. */
function Interaction({ it, compact }: { it: RoomItem; compact?: boolean }) {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [stats, setStats] = useState({ reactions: it.reactions, mine: it.mine, commentCount: it.commentCount });
  const comments = useQuery({ queryKey: ['comments', it.id], queryFn: () => api.get<RoomComment[]>(`/publications/${it.id}/comments`), enabled: open });
  const react = useMutation({ mutationFn: (kind: ReactionKind) => api.post<{ on: boolean; reactions: Record<string, number>; mine: ReactionKind[] }>(`/publications/${it.id}/reactions`, { kind }), onSuccess: (d) => setStats((s) => ({ ...s, reactions: d.reactions, mine: d.mine })) });
  const comment = useMutation({ mutationFn: () => api.post(`/publications/${it.id}/comments`, { text }), onSuccess: () => { setText(''); setStats((s) => ({ ...s, commentCount: s.commentCount + 1 })); void qc.invalidateQueries({ queryKey: ['comments', it.id] }); } });
  return (
    <div className="rr-finding__foot" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--rr-space-2)' }}>
      <div className="rr-row rr-row--wrap" style={{ gap: 6 }}>
        {REACTION_KINDS.map((k) => (
          <button key={k} type="button" className="rr-react" aria-pressed={stats.mine.includes(k)} onClick={() => react.mutate(k)}>
            {REACTION_KIND_LABELS[k]}{stats.reactions[k] ? <b>{stats.reactions[k]}</b> : null}
          </button>
        ))}
        <button type="button" className="rr-react" onClick={() => setOpen(!open)}>{compact ? <Icon name="comment" size={14} /> : 'comentar'}{stats.commentCount ? <b>{stats.commentCount}</b> : null}</button>
      </div>
      {open && (
        <div className="rr-thread">
          {comments.data?.map((c) => (
            <div key={c.id} className="rr-comment"><Avatar name={c.author.name} accent={c.author.id === me?.id} /><div><div className="rr-comment__who">{c.author.name} · {formatDate(c.createdAt)}</div><div className="rr-comment__text">{c.text}</div></div></div>
          ))}
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="o que você vê aqui?" />
          <div className="rr-row"><Button size="sm" variant="primary" disabled={text.trim().length < 1 || comment.isPending} onClick={() => comment.mutate()}>comentar</Button><Button size="sm" variant="quiet" onClick={() => setOpen(false)}>fechar</Button></div>
        </div>
      )}
    </div>
  );
}
